import React from "react"
import { Button, Dropdown } from "antd"
import type { MenuProps } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faWandMagicSparkles,
  faTerminal,
  faShuffle,
  faHardDrive,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons"
import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import {
  useQueryConsoleLayoutStore,
  QueryConsoleViewMode as ViewMode,
} from "stores/queryConsoleLayoutStore"

interface TabDef {
  mode: ViewMode
  label: string
  icon: IconDefinition
}

/** Modes shown in the dropdown. "Saved queries" (browsing everything saved
 *  to the project) is a view of its own, so it sits here with the others. */
const TABS: TabDef[] = [
  { mode: "builder", label: "Builder", icon: faWandMagicSparkles },
  { mode: "transform", label: "Transform", icon: faShuffle },
  { mode: "console", label: "Console", icon: faTerminal },
  { mode: "saved", label: "Saved queries", icon: faHardDrive },
]

/**
 * View-mode selector — compact dropdown that condenses what was previously a
 * tab ribbon. Shows the current mode (icon + label) on the trigger; clicking
 * opens a menu of the available modes.
 */
const QueryConsoleViewMode: React.FC = () => {
  const viewMode = useQueryConsoleLayoutStore((s) => s.viewMode)
  const setViewMode = useQueryConsoleLayoutStore((s) => s.setViewMode)

  const current = TABS.find((t) => t.mode === viewMode) ?? TABS[0]

  const items: MenuProps["items"] = TABS.map((tab) => ({
    key: tab.mode,
    label: (
      <span className="qc-view-mode__menu-item">
        <FontAwesomeIcon icon={tab.icon} className="qc-view-mode__icon" />
        {tab.label}
      </span>
    ),
    onClick: () => setViewMode(tab.mode),
  }))

  return (
    <Dropdown
      menu={{ items, selectedKeys: [current.mode] }}
      trigger={["click"]}
      placement="bottomLeft"
      autoAdjustOverflow={false}
      overlayClassName="qc-view-mode-overlay"
    >
      <Button aria-label="Change view mode">
        <FontAwesomeIcon icon={current.icon} className="qc-view-mode__icon" />
        <span className="qc-view-mode__label">{current.label}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className="qc-view-mode__caret"
        />
      </Button>
    </Dropdown>
  )
}

export default QueryConsoleViewMode
